import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Users, BookOpen, FileText, LineChart, Mail, MessageSquare, Download, Check, Quote, UserPlus, Star, CalendarDays, Pencil, Bot } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { fileToResizedDataURL, fmtEventDate } from "@/lib/image";
import { CourseBuilder } from "@/components/CourseBuilder";

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-border rounded-lg p-5" data-testid={`stat-${label.toLowerCase()}`}>
      <Icon className="h-5 w-5 text-gold" />
      <p className="font-display text-3xl font-bold text-navy mt-3">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{label}</p>
    </div>
  );
}

function CrudPanel({ kind, fields, columns }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const [open, setOpen] = useState(false);

  const load = () => api.get(`/${kind}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    fields.forEach((f) => {
      if (f.type === "list") payload[f.key] = (form[f.key] || "").split("\n").map((s) => s.trim()).filter(Boolean);
      if (f.type === "number") payload[f.key] = parseInt(form[f.key] || 0, 10);
    });
    try {
      await api.post(`/admin/${kind}`, payload);
      toast.success("Saved.");
      setForm({}); setOpen(false); load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const del = async (slug) => {
    try { await api.delete(`/admin/${kind}/${slug}`); toast.success("Deleted."); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">{items.length} {kind}</p>
        <button onClick={() => setOpen((o) => !o)} data-testid={`add-${kind}`} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-navy text-white text-sm font-medium hover:bg-navy-light transition-colors">
          <Plus className="h-4 w-4" /> {open ? "Close" : `Add ${kind.slice(0, -1)}`}
        </button>
      </div>

      {open && (
        <form onSubmit={save} className="bg-subtle border border-border rounded-lg p-6 mb-6 grid sm:grid-cols-2 gap-4" data-testid={`form-${kind}`}>
          {fields.map((f) => (
            <div key={f.key} className={f.type === "list" || f.full ? "sm:col-span-2" : ""}>
              <label className="text-xs text-slate-500">{f.label}</label>
              {f.type === "list" || f.type === "textarea" ? (
                <textarea rows={3} value={form[f.key] || ""} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))} placeholder={f.hint} className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" />
              ) : (
                <input required={f.required} type={f.type === "number" ? "number" : "text"} value={form[f.key] || ""} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))} className="w-full mt-1 h-10 rounded-md border border-border px-3 text-sm outline-none focus:ring-2 focus:ring-gold" />
              )}
            </div>
          ))}
          <button type="submit" className="sm:col-span-2 h-10 rounded-md bg-gold text-navy font-semibold text-sm hover:bg-gold-soft transition-colors" data-testid={`save-${kind}`}>Save</button>
        </form>
      )}

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-subtle text-left text-xs text-slate-500">
            <tr>{columns.map((c) => <th key={c} className="px-4 py-3 font-medium">{c}</th>)}<th className="px-4 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((it) => (
              <tr key={it.slug} data-testid={`row-${it.slug}`}>
                {columns.map((c) => <td key={c} className="px-4 py-3 text-slate-700">{String(it[c] ?? "")}</td>)}
                <td className="px-4 py-3 text-right">
                  <button onClick={() => del(it.slug)} data-testid={`delete-${it.slug}`} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DeletionsPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/deletion-requests").then((r) => setRows(r.data)).catch((err) => toast.error(apiErr(err)));
  }, []);
  const fmt = (d) => { if (!d) return "—"; const dt = new Date(d); return isNaN(dt) ? String(d) : dt.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); };
  return (
    <div data-testid="deletions-panel">
      <p className="text-sm text-slate-500 mb-4">{rows.length} deletion requests</p>
      <div className="bg-white border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-subtle text-left text-xs text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Identifier</th>
              <th className="px-4 py-3 font-medium">Matched</th>
              <th className="px-4 py-3 font-medium">Source</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Requested</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r, i) => (
              <tr key={i} data-testid={`deletion-row-${i}`}>
                <td className="px-4 py-3 text-slate-700">{r.identifier}</td>
                <td className="px-4 py-3">{r.matched ? <span className="text-xs font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">Yes</span> : <span className="text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-500">No</span>}</td>
                <td className="px-4 py-3 text-slate-500 capitalize">{r.source || "—"}</td>
                <td className="px-4 py-3 text-slate-500 capitalize">{r.status || "—"}</td>
                <td className="px-4 py-3 text-slate-500">{fmt(r.created_at)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No deletion requests yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UsersPanel() {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch((err) => toast.error(apiErr(err)));
  }, []);
  const fmt = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return isNaN(dt) ? String(d) : dt.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };
  const exportCsv = () => {
    const rows = [["Name", "Email", "Mobile", "Method", "Consent", "Consent At", "Terms Version", "Joined"],
      ...users.map((u) => [u.name || "", u.email || "", u.mobile || "", u.auth_method || "", u.terms_accepted ? "Yes" : "No", u.terms_accepted_at || "", u.terms_version || "", u.created_at || ""])];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a"); a.href = url; a.download = `krvprime-users-${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <div data-testid="users-panel">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">{users.length} registered users</p>
        <button onClick={exportCsv} disabled={users.length === 0} data-testid="export-users-csv" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-navy text-white text-sm font-medium hover:bg-navy-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>
      <div className="bg-white border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-subtle text-left text-xs text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Email / Mobile</th>
              <th className="px-4 py-3 font-medium">Method</th>
              <th className="px-4 py-3 font-medium">Terms Accepted</th>
              <th className="px-4 py-3 font-medium">Consent Date</th>
              <th className="px-4 py-3 font-medium">Version</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.map((u, i) => (
              <tr key={u.id || i} data-testid={`user-row-${i}`}>
                <td className="px-4 py-3 text-slate-700">{u.name || "—"}</td>
                <td className="px-4 py-3 text-slate-500">{u.email || u.mobile || "—"}</td>
                <td className="px-4 py-3 text-slate-500 capitalize">{u.auth_method || "—"}</td>
                <td className="px-4 py-3">
                  {u.terms_accepted
                    ? <span className="text-xs font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800" data-testid={`user-consent-${i}`}>Yes</span>
                    : <span className="text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-500" data-testid={`user-consent-${i}`}>No</span>}
                </td>
                <td className="px-4 py-3 text-slate-500">{fmt(u.terms_accepted_at)}</td>
                <td className="px-4 py-3 text-slate-500">{u.terms_version || "—"}</td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No users yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function SubscribersPanel() {
  const [subs, setSubs] = useState([]);

  useEffect(() => {
    api.get("/admin/subscribers").then((r) => setSubs(r.data)).catch((err) => toast.error(apiErr(err)));
  }, []);

  const fmt = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return isNaN(dt) ? String(d) : dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const exportCsv = () => {
    const rows = [["Email", "Name", "Subscribed At"], ...subs.map((s) => [s.email, s.name || "", s.created_at || ""])];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `krvprime-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="subscribers-panel">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">{subs.length} subscribers</p>
        <button onClick={exportCsv} disabled={subs.length === 0} data-testid="export-subscribers-csv" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-navy text-white text-sm font-medium hover:bg-navy-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>
      <div className="bg-white border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-subtle text-left text-xs text-slate-500">
            <tr><th className="px-4 py-3 font-medium">Email</th><th className="px-4 py-3 font-medium">Subscribed On</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {subs.map((s, i) => (
              <tr key={i} data-testid={`subscriber-row-${i}`}>
                <td className="px-4 py-3 text-slate-700">{s.email}</td>
                <td className="px-4 py-3 text-slate-500">{fmt(s.created_at)}</td>
              </tr>
            ))}
            {subs.length === 0 && <tr><td colSpan={2} className="px-4 py-8 text-center text-slate-400">No subscribers yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TestimonialsPanel() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data)).catch((err) => toast.error(apiErr(err)));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const approve = async (id) => {
    try { await api.post(`/admin/testimonials/${id}/approve`); toast.success("Approved — now live on homepage."); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted."); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <div data-testid="testimonials-panel">
      <p className="text-sm text-slate-500 mb-4">{items.length} testimonials · {items.filter((t) => t.status === "approved").length} approved</p>
      <div className="space-y-3">
        {items.map((t) => (
          <div key={t.id} className="bg-white border border-border rounded-lg p-5 flex items-start gap-4" data-testid={`admin-testimonial-${t.id}`}>
            <Quote className="h-5 w-5 text-gold shrink-0 mt-1" />
            <div className="flex-1">
              <p className="text-sm text-slate-700 leading-relaxed">{t.quote}</p>
              <p className="mt-2 text-xs text-slate-500"><span className="font-semibold text-navy">{t.name}</span>{t.role ? ` · ${t.role}` : ""}</p>
            </div>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-md ${t.status === "approved" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{t.status}</span>
            <div className="flex items-center gap-2">
              {t.status !== "approved" && (
                <button onClick={() => approve(t.id)} data-testid={`approve-testimonial-${t.id}`} className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-navy text-white text-xs font-medium hover:bg-navy-light transition-colors"><Check className="h-3.5 w-3.5" /> Approve</button>
              )}
              <button onClick={() => del(t.id)} data-testid={`delete-testimonial-${t.id}`} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="bg-white border border-border rounded-lg px-4 py-8 text-center text-slate-400">No testimonials submitted yet.</div>}
      </div>
    </div>
  );
}

function LeadsPanel() {
  const [leads, setLeads] = useState([]);
  useEffect(() => {
    api.get("/admin/leads").then((r) => setLeads(r.data)).catch((err) => toast.error(apiErr(err)));
  }, []);
  const fmt = (d) => { const t = new Date(d); return isNaN(t) ? "—" : t.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); };
  const exportCsv = () => {
    const rows = [["Name", "Email", "Phone", "Interest", "Captured"], ...leads.map((l) => [l.name, l.email, l.phone || "", l.interest || "", l.created_at || ""])];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `krvprime-leads-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div data-testid="leads-panel">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">{leads.length} leads captured via the Assistant</p>
        <button onClick={exportCsv} disabled={leads.length === 0} data-testid="export-leads-csv" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-navy text-white text-sm font-medium hover:bg-navy-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>
      <div className="bg-white border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-subtle text-left text-xs text-slate-500">
            <tr><th className="px-4 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium">Email</th><th className="px-4 py-3 font-medium">Phone</th><th className="px-4 py-3 font-medium">Interest</th><th className="px-4 py-3 font-medium">Captured</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {leads.map((l, i) => (
              <tr key={i} data-testid={`lead-row-${i}`}>
                <td className="px-4 py-3 text-navy font-medium">{l.name}</td>
                <td className="px-4 py-3 text-slate-700">{l.email}</td>
                <td className="px-4 py-3 text-slate-600">{l.phone || "—"}</td>
                <td className="px-4 py-3 text-slate-500">{l.interest || "—"}</td>
                <td className="px-4 py-3 text-slate-500">{fmt(l.created_at)}</td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No leads captured yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReviewsPanel() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/reviews").then((r) => setItems(r.data)).catch((err) => toast.error(apiErr(err)));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const approve = async (id) => { try { await api.post(`/admin/reviews/${id}/approve`); toast.success("Approved — now live on the course."); load(); } catch (err) { toast.error(apiErr(err)); } };
  const del = async (id) => { try { await api.delete(`/admin/reviews/${id}`); toast.success("Deleted."); load(); } catch (err) { toast.error(apiErr(err)); } };
  return (
    <div data-testid="reviews-panel">
      <p className="text-sm text-slate-500 mb-4">{items.length} reviews · {items.filter((r) => r.status === "approved").length} approved</p>
      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.id} className="bg-white border border-border rounded-lg p-5 flex items-start gap-4" data-testid={`admin-review-${r.id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="inline-flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-4 w-4 ${n <= r.rating ? "text-gold fill-gold" : "text-slate-300"}`} />)}</span>
                <span className="text-xs text-slate-400">· {r.course_title}</span>
              </div>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">{r.comment}</p>
              <p className="mt-1 text-xs font-semibold text-navy">{r.user_name}</p>
            </div>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-md ${r.status === "approved" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{r.status}</span>
            <div className="flex items-center gap-2">
              {r.status !== "approved" && <button onClick={() => approve(r.id)} data-testid={`approve-review-${r.id}`} className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-navy text-white text-xs font-medium hover:bg-navy-light transition-colors"><Check className="h-3.5 w-3.5" /> Approve</button>}
              <button onClick={() => del(r.id)} data-testid={`delete-review-${r.id}`} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="bg-white border border-border rounded-lg px-4 py-8 text-center text-slate-400">No reviews submitted yet.</div>}
      </div>
    </div>
  );
}

function EventsPanel({ onStatsChange }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [regEvent, setRegEvent] = useState(null);
  const [regs, setRegs] = useState([]);

  const load = () => api.get("/admin/events").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => setForm({ mode: "online", status: "published", capacity: 0, price: 0, event_type: "live", recording_type: "youtube" });
  const setF = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onCover = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const url = await fileToResizedDataURL(f, { max: 1000 }); setForm((s) => ({ ...s, cover_image: url })); }
    catch { toast.error("Could not read image."); }
  };

  const onRecording = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, recording_type: "file", recording_url: data.url }));
      toast.success("Recording uploaded.");
    } catch (err) { toast.error(apiErr(err)); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, capacity: parseInt(form.capacity || 0, 10), price: parseInt(form.price || 0, 10) };
      await api.post("/admin/events", payload);
      toast.success("Event saved."); setForm(null); load(); onStatsChange?.();
    } catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };

  const del = async (id) => { try { await api.delete(`/admin/events/${id}`); toast.success("Deleted."); load(); onStatsChange?.(); } catch (e) { toast.error(apiErr(e)); } };
  const viewRegs = async (ev) => { setRegEvent(ev); try { const { data } = await api.get(`/admin/events/${ev.id}/registrations`); setRegs(data); } catch (e) { toast.error(apiErr(e)); } };

  const F = ({ k, label, type = "text", full = false }) => (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-xs text-slate-500">{label}</label>
      {type === "textarea"
        ? <textarea rows={3} value={form[k] || ""} onChange={setF(k)} data-testid={`event-${k}`} className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" />
        : <input type={type} value={form[k] || ""} onChange={setF(k)} data-testid={`event-${k}`} className="w-full mt-1 h-10 rounded-md border border-border px-3 text-sm outline-none focus:ring-2 focus:ring-gold" />}
    </div>
  );

  return (
    <div data-testid="events-panel">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">{items.length} events</p>
        <button onClick={() => (form ? setForm(null) : startNew())} data-testid="add-event" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-navy text-white text-sm font-medium hover:bg-navy-light transition-colors">
          <Plus className="h-4 w-4" /> {form ? "Close" : "Add event"}
        </button>
      </div>

      {form && (
        <form onSubmit={save} className="bg-subtle border border-border rounded-lg p-6 mb-6 grid sm:grid-cols-2 gap-4" data-testid="form-events">
          {F({ k: "title", label: "Title", full: true })}
          <div className="sm:col-span-2">
            <label className="text-xs text-slate-500">Type</label>
            <select value={form.event_type || "live"} onChange={setF("event_type")} data-testid="event-type" className="w-full mt-1 h-10 rounded-md border border-border px-3 text-sm bg-white outline-none focus:ring-2 focus:ring-gold">
              <option value="live">Live webinar / event</option>
              <option value="recorded">Recorded (on-demand, to purchase)</option>
            </select>
          </div>
          {form.event_type === "recorded" && (
            <div className="sm:col-span-2 grid sm:grid-cols-2 gap-4 border border-gold/40 rounded-md p-4 bg-white">
              <div>
                <label className="text-xs text-slate-500">Recording source</label>
                <select value={form.recording_type || "youtube"} onChange={setF("recording_type")} data-testid="event-recording-type" className="w-full mt-1 h-10 rounded-md border border-border px-3 text-sm bg-white outline-none focus:ring-2 focus:ring-gold">
                  <option value="youtube">YouTube</option>
                  <option value="vimeo">Vimeo</option>
                  <option value="file">Uploaded file</option>
                </select>
              </div>
              {form.recording_type === "file" ? (
                <div>
                  <label className="text-xs text-slate-500">Upload recording</label>
                  <input type="file" accept="video/*" onChange={onRecording} data-testid="event-recording-file" className="block mt-1 text-sm" />
                  {form.recording_url && <span className="text-xs text-emerald-600 block mt-1">Uploaded ✓</span>}
                </div>
              ) : (
                F({ k: "recording_url", label: "Recording link (YouTube / Vimeo)" })
              )}
            </div>
          )}
          {F({ k: "speaker", label: "Speaker" })}
          {F({ k: "duration", label: "Duration (e.g. 90 min)" })}
          <div>
            <label className="text-xs text-slate-500">Date & time</label>
            <input type="datetime-local" value={(form.starts_at || "").slice(0, 16)} onChange={setF("starts_at")} data-testid="event-starts-at" className="w-full mt-1 h-10 rounded-md border border-border px-3 text-sm outline-none focus:ring-2 focus:ring-gold" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Mode</label>
            <select value={form.mode} onChange={setF("mode")} data-testid="event-mode" className="w-full mt-1 h-10 rounded-md border border-border px-3 text-sm bg-white outline-none focus:ring-2 focus:ring-gold">
              <option value="online">Online</option>
              <option value="offline">In-person</option>
            </select>
          </div>
          {form.mode === "online" ? F({ k: "join_link", label: "Join link (hidden until registered)", full: true }) : F({ k: "location", label: "Venue / location", full: true })}
          {F({ k: "capacity", label: "Capacity (0 = unlimited)", type: "number" })}
          {F({ k: "price", label: "Price ₹ (0 = free)", type: "number" })}
          <div>
            <label className="text-xs text-slate-500">Status</label>
            <select value={form.status} onChange={setF("status")} data-testid="event-status" className="w-full mt-1 h-10 rounded-md border border-border px-3 text-sm bg-white outline-none focus:ring-2 focus:ring-gold">
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
          {F({ k: "description", label: "Description", type: "textarea", full: true })}
          <div className="sm:col-span-2">
            <label className="text-xs text-slate-500">Cover image</label>
            <input type="file" accept="image/*" onChange={onCover} data-testid="event-cover" className="block mt-1 text-sm" />
            {form.cover_image && <img src={form.cover_image} alt="cover" className="mt-2 h-28 rounded-md object-cover" />}
          </div>
          <button type="submit" disabled={saving} className="sm:col-span-2 h-10 rounded-md bg-gold text-navy font-semibold text-sm hover:bg-gold-soft transition-colors disabled:opacity-60" data-testid="save-events">{saving ? "Saving…" : "Save event"}</button>
        </form>
      )}

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-subtle text-left text-xs text-slate-500">
            <tr><th className="px-4 py-3 font-medium">Title</th><th className="px-4 py-3 font-medium">When</th><th className="px-4 py-3 font-medium">Mode</th><th className="px-4 py-3 font-medium">Regs</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((ev) => (
              <tr key={ev.id} data-testid={`event-row-${ev.id}`}>
                <td className="px-4 py-3 text-slate-700">{ev.title}</td>
                <td className="px-4 py-3 text-slate-500">{fmtEventDate(ev.starts_at)}</td>
                <td className="px-4 py-3 text-slate-500 capitalize">{ev.event_type === "recorded" ? "Recorded" : (ev.mode === "online" ? "Live · Online" : "Live · In-person")}</td>
                <td className="px-4 py-3"><button onClick={() => viewRegs(ev)} data-testid={`view-regs-${ev.id}`} className="text-navy font-medium hover:text-gold">{ev.registrations_count}</button></td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${ev.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>{ev.status}</span></td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setForm({ ...ev })} data-testid={`edit-event-${ev.id}`} className="text-slate-500 hover:text-navy mr-3"><Pencil className="h-4 w-4 inline" /></button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid={`delete-event-${ev.id}`} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4 inline" /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this event?</AlertDialogTitle>
                        <AlertDialogDescription>Deleting "{ev.title}" also removes all {ev.registrations_count} registration(s). This cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`delete-event-cancel-${ev.id}`}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(ev.id)} data-testid={`delete-event-confirm-${ev.id}`} className="bg-red-600 hover:bg-red-700">Delete event</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No events yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!regEvent} onOpenChange={(o) => { if (!o) setRegEvent(null); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="event-regs-dialog">
          <DialogHeader><DialogTitle>Registrations · {regEvent?.title}</DialogTitle></DialogHeader>
          {regs.length === 0 ? <p className="text-slate-500 py-4 text-center">No registrations yet.</p> : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-slate-500"><tr><th className="py-2">Name</th><th className="py-2">Email</th><th className="py-2">Mobile</th></tr></thead>
              <tbody className="divide-y divide-border">
                {regs.map((r, i) => <tr key={i}><td className="py-2">{r.name || "—"}</td><td className="py-2">{r.email || "—"}</td><td className="py-2">{r.mobile || "—"}</td></tr>)}
              </tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AssistantPanel() {
  const [data, setData] = useState(null);
  const [openSid, setOpenSid] = useState(null);
  useEffect(() => {
    api.get("/admin/assistant/conversations").then((r) => setData(r.data)).catch((err) => toast.error(apiErr(err)));
  }, []);
  const fmt = (d) => { if (!d) return "—"; const dt = new Date(d); return isNaN(dt) ? String(d) : dt.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); };
  if (!data) return <p className="text-sm text-slate-400" data-testid="assistant-panel-loading">Loading conversations…</p>;
  return (
    <div data-testid="assistant-panel">
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-border rounded-lg p-4"><p className="font-display text-2xl font-bold text-navy" data-testid="assistant-stat-sessions">{data.total_sessions}</p><p className="text-xs text-slate-500 mt-1">Conversations</p></div>
        <div className="bg-white border border-border rounded-lg p-4"><p className="font-display text-2xl font-bold text-navy" data-testid="assistant-stat-questions">{data.total_questions}</p><p className="text-xs text-slate-500 mt-1">Questions asked</p></div>
        <div className="bg-white border border-border rounded-lg p-4"><p className="font-display text-2xl font-bold text-gold" data-testid="assistant-stat-leads">{data.lead_triggers}</p><p className="text-xs text-slate-500 mt-1">Lead-form triggers</p></div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h3 className="font-display font-semibold text-navy mb-3">Recent questions</h3>
          <div className="bg-white border border-border rounded-lg divide-y divide-border max-h-[520px] overflow-y-auto">
            {data.recent_questions.map((q, i) => (
              <div key={i} className="px-4 py-3" data-testid={`assistant-question-${i}`}>
                <p className="text-sm text-slate-700">{q.text}</p>
                <p className="text-xs text-slate-400 mt-1">{fmt(q.ts)}</p>
              </div>
            ))}
            {data.recent_questions.length === 0 && <div className="px-4 py-8 text-center text-slate-400 text-sm">No questions yet.</div>}
          </div>
        </div>
        <div>
          <h3 className="font-display font-semibold text-navy mb-3">Conversations</h3>
          <div className="bg-white border border-border rounded-lg divide-y divide-border max-h-[520px] overflow-y-auto">
            {data.sessions.map((s) => (
              <div key={s.session_id} data-testid={`assistant-session-${s.session_id}`}>
                <button onClick={() => setOpenSid(openSid === s.session_id ? null : s.session_id)} className="w-full text-left px-4 py-3 hover:bg-subtle transition-colors flex items-center justify-between gap-3">
                  <span className="text-sm text-slate-700 truncate">{(s.messages.find((m) => m.role === "user") || {}).text || "Conversation"}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    {s.lead_triggered && <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800">LEAD</span>}
                    <span className="text-xs text-slate-400">{s.message_count} Q</span>
                  </span>
                </button>
                {openSid === s.session_id && (
                  <div className="px-4 pb-4 space-y-2 bg-subtle/40">
                    {s.messages.map((m, i) => (
                      <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
                        <span className={`inline-block text-sm px-3 py-2 rounded-lg max-w-[85%] ${m.role === "user" ? "bg-navy text-white" : "bg-white border border-border text-slate-700"}`}>{m.text}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {data.sessions.length === 0 && <div className="px-4 py-8 text-center text-slate-400 text-sm">No conversations yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data));
  useEffect(() => {
    loadStats();
    api.get("/admin/contacts").then((r) => setContacts(r.data));
  }, []);

  return (
    <div data-testid="admin-page">
      <section className="bg-navy grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="eyebrow text-gold">Admin</p>
          <h1 className="font-display text-3xl font-bold text-white mt-2">KRV Prime Admin Dashboard</h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-12">
            <StatCard icon={Users} label="Users" value={stats.users} />
            <StatCard icon={BookOpen} label="Courses" value={stats.courses} />
            <StatCard icon={LineChart} label="Research" value={stats.research} />
            <StatCard icon={FileText} label="Articles" value={stats.articles} />
            <StatCard icon={BookOpen} label="Enrollments" value={stats.enrollments} />
            <StatCard icon={Mail} label="Subscribers" value={stats.subscribers} />
            <StatCard icon={MessageSquare} label="Contacts" value={stats.contacts} />
            <StatCard icon={Quote} label="Testimonials" value={stats.testimonials} />
            <StatCard icon={UserPlus} label="Leads" value={stats.leads} />
            <StatCard icon={Star} label="Reviews" value={stats.reviews} />
            <StatCard icon={CalendarDays} label="Events" value={stats.events} />
            <StatCard icon={Bot} label="Assistant" value={stats.assistant_sessions} />
          </div>
        )}

        <Tabs defaultValue="courses" data-testid="admin-tabs">
          <TabsList className="bg-subtle">
            <TabsTrigger value="courses" data-testid="admin-tab-courses">Courses</TabsTrigger>
            <TabsTrigger value="research" data-testid="admin-tab-research">Research</TabsTrigger>
            <TabsTrigger value="articles" data-testid="admin-tab-articles">Articles</TabsTrigger>
            <TabsTrigger value="strategies" data-testid="admin-tab-strategies">Strategies</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="admin-tab-contacts">Contacts</TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="admin-tab-subscribers">Subscribers</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
            <TabsTrigger value="deletions" data-testid="admin-tab-deletions">Deletions</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="admin-tab-testimonials">Testimonials</TabsTrigger>
            <TabsTrigger value="leads" data-testid="admin-tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="admin-tab-reviews">Reviews</TabsTrigger>
            <TabsTrigger value="events" data-testid="admin-tab-events">Events</TabsTrigger>
            <TabsTrigger value="assistant" data-testid="admin-tab-assistant">Assistant</TabsTrigger>
          </TabsList>

          <TabsContent value="courses" className="mt-6">
            <CourseBuilder />
          </TabsContent>

          <TabsContent value="research" className="mt-6">
            <CrudPanel kind="research" columns={["title", "category", "published_date"]} fields={[
              { key: "slug", label: "Slug", required: true }, { key: "title", label: "Title", required: true },
              { key: "category", label: "Category" }, { key: "index_tag", label: "Index Tag" },
              { key: "published_date", label: "Published (YYYY-MM-DD)" }, { key: "data_as_of", label: "Data As Of" },
              { key: "author", label: "Author" }, { key: "reading_time", label: "Reading Time" },
              { key: "summary", label: "Summary", type: "textarea" }, { key: "data_source", label: "Data Source" },
              { key: "methodology", label: "Methodology", type: "textarea" }, { key: "risk_notes", label: "Risk Notes", type: "textarea" },
              { key: "content", label: "Content paragraphs (one per line)", type: "list" },
            ]} />
          </TabsContent>

          <TabsContent value="articles" className="mt-6">
            <CrudPanel kind="articles" columns={["title", "category", "author", "date"]} fields={[
              { key: "slug", label: "Slug", required: true }, { key: "title", label: "Title", required: true },
              { key: "category", label: "Category" }, { key: "author", label: "Author" },
              { key: "date", label: "Date (YYYY-MM-DD)" }, { key: "reading_time", label: "Reading Time" },
              { key: "excerpt", label: "Excerpt", type: "textarea" },
              { key: "tags", label: "Tags (one per line)", type: "list" },
              { key: "content", label: "Content paragraphs (one per line)", type: "list" },
            ]} />
          </TabsContent>

          <TabsContent value="strategies" className="mt-6">
            <CrudPanel kind="strategies" columns={["name", "condition", "slug"]} fields={[
              { key: "slug", label: "Slug (e.g. cpr-pivot-points)", required: true },
              { key: "name", label: "Name", required: true },
              { key: "condition", label: "Best studied in (e.g. Trend, Breakout)" },
              { key: "concept", label: "Concept", type: "textarea", full: true },
              { key: "market_conditions", label: "Market Conditions (leave blank for default)", type: "textarea", full: true },
              { key: "components", label: "Components (leave blank for default)", type: "textarea", full: true },
              { key: "rules", label: "Rules (leave blank for default)", type: "textarea", full: true },
              { key: "case_study", label: "Educational Case Study (leave blank for default)", type: "textarea", full: true },
              { key: "risk_considerations", label: "Risk Considerations (leave blank for default)", type: "textarea", full: true },
              { key: "limitations", label: "Limitations (leave blank for default)", type: "textarea", full: true },
            ]} />
          </TabsContent>

          <TabsContent value="contacts" className="mt-6">
            <div className="bg-white border border-border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-subtle text-left text-xs text-slate-500">
                  <tr><th className="px-4 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium">Email</th><th className="px-4 py-3 font-medium">Category</th><th className="px-4 py-3 font-medium">Message</th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {contacts.map((c) => (
                    <tr key={c.id}><td className="px-4 py-3">{c.name}</td><td className="px-4 py-3">{c.email}</td><td className="px-4 py-3">{c.category}</td><td className="px-4 py-3 max-w-xs truncate">{c.message}</td></tr>
                  ))}
                  {contacts.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No contact submissions yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="subscribers" className="mt-6">
            <SubscribersPanel />
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <UsersPanel />
          </TabsContent>

          <TabsContent value="deletions" className="mt-6">
            <DeletionsPanel />
          </TabsContent>

          <TabsContent value="testimonials" className="mt-6">
            <TestimonialsPanel />
          </TabsContent>

          <TabsContent value="leads" className="mt-6">
            <LeadsPanel />
          </TabsContent>

          <TabsContent value="reviews" className="mt-6">
            <ReviewsPanel />
          </TabsContent>

          <TabsContent value="events" className="mt-6">
            <EventsPanel onStatsChange={loadStats} />
          </TabsContent>

          <TabsContent value="assistant" className="mt-6">
            <AssistantPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
